// A customer made and changed in the app (plan chunk C9, decision 184): the name, the kind of place, when it gets
// deliveries, its language, a call before the truck comes, notes; put away and brought back by the owner or the office.
import type { Customer, CustomerCreate } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, PageHead } from '../components/ui';
import { weekdayName } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const DAYS = [1, 2, 3, 4, 5, 6, 7];
/** The kinds the office already uses, offered as you type (any other word is kept as typed). */
const KINDS = ['restaurant', 'store', 'school', 'caterer', 'food truck', 'other'];

type Fields = { name: string; shortName: string; customerType: string; deliveryDays: number[]; language: 'en' | 'es'; callAhead: boolean; notes: string };

function DetailsFields({ f, set }: { f: Fields; set: (next: Partial<Fields>) => void }) {
  const { t, intlLocale } = useLocale();
  return (
    <>
      <label className="field">
        <span>{t('name')}</span>
        <input type="text" value={f.name} maxLength={120} required autoComplete="off" onChange={(e) => set({ name: e.target.value })} />
      </label>
      <label className="field">
        <span>{t('customerShortName')}</span>
        <input type="text" value={f.shortName} maxLength={40} autoComplete="off" onChange={(e) => set({ shortName: e.target.value })} />
      </label>
      <label className="field">
        <span>{t('customerTypeLabel')}</span>
        <input type="text" value={f.customerType} maxLength={60} autoComplete="off" list="customer-kinds" onChange={(e) => set({ customerType: e.target.value })} />
        <datalist id="customer-kinds">
          {KINDS.map((k) => (
            <option key={k} value={k} />
          ))}
        </datalist>
      </label>
      <fieldset className="field">
        <legend>{t('deliveryDays')}</legend>
        <div className="chips">
          {DAYS.map((d) => (
            <button
              key={d}
              type="button"
              className="chip"
              aria-pressed={f.deliveryDays.includes(d)}
              onClick={() => set({ deliveryDays: f.deliveryDays.includes(d) ? f.deliveryDays.filter((x) => x !== d) : [...f.deliveryDays, d].sort((a, b) => a - b) })}
            >
              {weekdayName(d, intlLocale)}
            </button>
          ))}
        </div>
      </fieldset>
      <label className="field">
        <span>{t('language')}</span>
        <select value={f.language} onChange={(e) => set({ language: e.target.value === 'es' ? 'es' : 'en' })}>
          <option value="en">{t('lang_en')}</option>
          <option value="es">{t('lang_es')}</option>
        </select>
      </label>
      <label className="check">
        <input type="checkbox" checked={f.callAhead} onChange={(e) => set({ callAhead: e.target.checked })} /> {t('callAheadLabel')}
      </label>
      <label className="field">
        <span>{t('notes')}</span>
        <textarea value={f.notes} maxLength={2000} rows={3} onChange={(e) => set({ notes: e.target.value })} />
      </label>
    </>
  );
}

/** Customers → New customer. The owner and the office also pick the price tier; sales adds at the standard tier. */
export function NewCustomerPage() {
  const { t } = useLocale();
  const { me } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setsMoney = me?.role === 'owner' || me?.role === 'office';
  const tiers = useQuery({ queryKey: qk.priceTiers, queryFn: ({ signal }) => api.priceTiers(signal), enabled: setsMoney, staleTime: 10 * 60_000 });
  const [f, setF] = useState<Fields>({ name: '', shortName: '', customerType: '', deliveryDays: [], language: 'en', callAhead: false, notes: '' });
  const [tier, setTier] = useState<Customer['priceTier']>('standard');
  const create = useMutation({
    mutationFn: (body: CustomerCreate) => api.createCustomer(body),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      qc.setQueryData(qk.customer(c.id), c);
      navigate(`/customers/${c.id}`, { replace: true });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!f.name.trim()) return;
    create.mutate({
      name: f.name.trim(),
      ...(f.shortName.trim() ? { shortName: f.shortName.trim() } : {}),
      ...(f.customerType.trim() ? { customerType: f.customerType.trim() } : {}),
      deliveryDays: f.deliveryDays,
      language: f.language,
      callAhead: f.callAhead,
      ...(f.notes.trim() ? { notes: f.notes.trim() } : {}),
      priceTier: setsMoney ? tier : 'standard',
      termsDays: 0,
      stage: 'customer',
    });
  };
  return (
    <form className="stack" onSubmit={submit} data-testid="new-customer">
      <PageHead title={t('newCustomer')} back="/customers" />
      <DetailsFields f={f} set={(n) => setF((cur) => ({ ...cur, ...n }))} />
      {setsMoney ? (
        <label className="field">
          <span>{t('priceTierLabel')}</span>
          <select value={tier} onChange={(e) => setTier(e.target.value as Customer['priceTier'])}>
            {(tiers.data ?? [{ code: 'standard', name: 'standard', active: true }]).filter((x) => x.active).map((x) => (
              <option key={x.code} value={x.code}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {create.error ? <ErrorBox error={create.error} /> : null}
      <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={create.isPending || !f.name.trim()}>
        {t('save')}
      </button>
    </form>
  );
}

/** On a customer's page: their details changed in place, and put away or brought back (owner and office). */
export function CustomerDetailsCard({ customer: c, sells, setsMoney }: { customer: Customer; sells: boolean; setsMoney: boolean }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const fromCustomer = (): Fields => ({ name: c.name, shortName: c.shortName ?? '', customerType: c.customerType ?? '', deliveryDays: c.deliveryDays, language: c.language, callAhead: c.callAhead, notes: c.notes ?? '' });
  const [f, setF] = useState<Fields>(fromCustomer);
  const put = (next: Customer) => {
    qc.setQueryData(qk.customer(c.id), next);
    void qc.invalidateQueries({ queryKey: ['customers'] });
  };
  const save = useMutation({
    mutationFn: () =>
      api.patchCustomer(c.id, {
        name: f.name.trim(),
        shortName: f.shortName.trim(),
        customerType: f.customerType.trim(),
        deliveryDays: f.deliveryDays,
        language: f.language,
        callAhead: f.callAhead,
        notes: f.notes.trim(),
      }),
    onSuccess: (next) => {
      put(next);
      setOpen(false);
    },
  });
  const archive = useMutation({ mutationFn: (away: boolean) => api.archiveCustomer(c.id, away), onSuccess: put });
  return (
    <section className="card stack-sm" data-testid="customer-details">
      {c.archived ? <p className="note">{t('customerArchived')}</p> : null}
      <div className="contact-actions">
        {sells && !c.archived ? (
          <button type="button" className="btn btn-secondary" onClick={() => { setF(fromCustomer()); setOpen((v) => !v); }}>
            {t('editDetails')}
          </button>
        ) : null}
        {setsMoney ? (
          !c.archived ? (
            <button type="button" className="btn btn-ghost" disabled={archive.isPending} onClick={() => { if (window.confirm(t('archiveCustomerConfirm').replace('{name}', c.name))) archive.mutate(true); }}>
              {t('archive')}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={archive.isPending} onClick={() => archive.mutate(false)}>
              {t('restoreBtn')}
            </button>
          )
        ) : null}
      </div>
      {archive.error ? <ErrorBox error={archive.error} /> : null}
      {open ? (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (f.name.trim()) save.mutate();
          }}
        >
          <DetailsFields f={f} set={(n) => setF((cur) => ({ ...cur, ...n }))} />
          {save.error ? <ErrorBox error={save.error} /> : null}
          <div className="contact-actions">
            <button type="submit" className="btn btn-primary" disabled={save.isPending || !f.name.trim()}>
              {t('save')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>
              {t('cancel')}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
